import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// One installable app per server (one PWA per client Chrome profile): on a server's pages, the page announces that
// server's manifest (« aidev <server> », starting on its page, its own icon), so Chrome installs it as its own app;
// elsewhere, the generic aidev one. The window takes the server's colour from that manifest.

const SERVER_PATH = /^\/servers\/([a-z0-9][a-z0-9-]{0,39})(?:\/|$)/;

function ensure<T extends HTMLElement>(selector: string, create: () => T): T {
  return (document.head.querySelector(selector) as T | null) ?? document.head.appendChild(create());
}

export function useServerApp(): void {
  const { pathname } = useLocation();
  const serverId = SERVER_PATH.exec(pathname)?.[1];
  useEffect(() => {
    const link = ensure<HTMLLinkElement>('link[rel="manifest"]', () => Object.assign(document.createElement('link'), { rel: 'manifest' }));
    const meta = ensure<HTMLMetaElement>('meta[name="theme-color"]', () => Object.assign(document.createElement('meta'), { name: 'theme-color' }));
    link.dataset.generic ??= link.getAttribute('href') ?? '/manifest.webmanifest';
    meta.dataset.generic ??= meta.content || '#080810';
    if (!serverId) {
      link.setAttribute('href', link.dataset.generic);
      meta.content = meta.dataset.generic;
      return;
    }
    const href = `/manifests/${serverId}.webmanifest`;
    link.setAttribute('href', href);
    let cancelled = false;
    fetch(href)
      .then((r) => (r.ok ? r.json() : null))
      .then((m: { theme_color?: string } | null) => {
        if (!cancelled && m?.theme_color) meta.content = m.theme_color;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [serverId]);
}
